import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const DocumentAttachments = ({ documents, onUpload }) => {
  const [dragActive, setDragActive] = useState(false);

  const getFileIcon = (type) => {
    const icons = {
      'pdf': 'FileText',
      'image': 'Image',
      'document': 'File',
      'spreadsheet': 'FileSpreadsheet'
    };
    return icons?.[type] || 'File';
  };

  const getFileColor = (type) => {
    const colors = {
      'pdf': 'text-error',
      'image': 'text-success',
      'document': 'text-primary',
      'spreadsheet': 'text-accent'
    };
    return colors?.[type] || 'text-muted-foreground';
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      onUpload(e?.dataTransfer?.files);
    }
  };

  const handleFileInput = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      onUpload(e?.target?.files);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Paperclip" size={20} color="var(--color-primary)" />
          <h2 className="text-lg font-semibold text-foreground">Document Attachments</h2>
        </div>
        <span className="text-sm text-muted-foreground">{documents?.length} files</span>
      </div>
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive ? 'border-primary bg-primary/5' : 'border-border'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Icon name="Upload" size={32} className="mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-foreground mb-1">Drag and drop files here</p>
        <p className="text-xs text-muted-foreground mb-3">or</p>
        <label>
          <input
            type="file"
            multiple
            onChange={handleFileInput}
            className="hidden"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          />
          <Button variant="outline" size="sm" asChild>
            <span>Browse Files</span>
          </Button>
        </label>
        <p className="text-xs text-muted-foreground mt-3">Supported: PDF, DOC, DOCX, JPG, PNG (Max 10MB)</p>
      </div>
      <div className="mt-6 space-y-2">
        {documents?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="FileX" size={48} className="mx-auto mb-2 opacity-20" />
            <p className="text-sm">No documents attached yet</p>
          </div>
        ) : (
          documents?.map((doc) => (
            <div key={doc?.id} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent/5 transition-colors">
              {doc?.type === 'image' ? (
                <div className="w-12 h-12 rounded overflow-hidden border border-border flex-shrink-0">
                  <Image 
                    src={doc?.url} 
                    alt={doc?.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className={`w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 ${getFileColor(doc?.type)}`}>
                  <Icon name={getFileIcon(doc?.type)} size={24} />
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-foreground truncate">{doc?.name}</h3>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{doc?.size}</span>
                  <span>•</span>
                  <span>{doc?.uploadedBy}</span>
                  <span>•</span>
                  <span>{doc?.uploadDate}</span>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button className="p-2 hover:bg-accent/10 rounded transition-colors">
                  <Icon name="Download" size={16} color="var(--color-primary)" />
                </button>
                <button className="p-2 hover:bg-error/10 rounded transition-colors">
                  <Icon name="Trash2" size={16} color="var(--color-error)" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DocumentAttachments;